/**
 * Common module for implementing NewContract functionality.
 * 
 * USAGE
 * 
 *     Create theme specific view and script file for it. Create NewContract module instance in it, and give root element as parameter.
 *     NewContract functionality applies inside that root element:
 *     
 *     export class ContractPage {
 *         // Instance of common NewContract module.
 *         newContract: NewContract;
 *     
 *         constructor(rootSelector: string) {
 *             this.newContract = new NewContract({
 *                 el: rootSelector,
 *                 phases: [
 *                     { name: "phase-new-contract", id: 1 },
 *                     { name: "phase-confirmation", id: 2 },
 *                 ],
 *                 preselects: { ... },
 *                 // Add Vue stuff specific to theme.
 *                 mixin: {
 *                     components: { PhaseNewContract, PhaseContractConfirmation }
 *                 }
 *             });
 *         }
 *     
 *         validationFailed(validationErrors: ValidationErrors) { ... }
 *     }
 *     
 *     var page = new ContractPage("#rootContainer");
 * 
 * VUEX STORE
 * 
 * 
 * DIRECTIVES
 * 
 *     v-new-contract-value:
 *     
 *     This directive makes HTML element to call NewContractHub to update given value whenever change is triggered from the element.
 *     Value key is determined from element's name attribute and the value from the element's value. For example:
 *     
 *         <input name="Phone" v-new-contract-value />
 *         
 *     Above automatically calls NewContractHub's SetCustomerValue("Phone", value).
 *     
 *     If there are ambiguity in key (e.g. key "MPStreetName" is handled both in SetCustomerValue and SetMeteringPointValue), it is possible to
 *     force the "type" of the key with directive modifier, for example:
 *     
 *         <input name="MPStreetName" v-new-contract-value.mp />
 *     
 *     Above calls the SetMeteringPointValue with key "MPStreetName".
 *     
 *     Possible modifiers are:
 *         mp:          SetMeteringPointValue
 *         contract:    SetContractValue
 *         customer:    SetCustomerValue
 *         basic:       SetValue
 */

require("core-js/features/array/find");
require("core-js/features/promise");
import Vue from "vue"
import { VNodeDirective, VNode } from "vue/types/vnode";
import { VueConstructor } from "vue/types/vue";
import { ComponentOptions } from "vue/types/options";
import { CustomerTypeEnum, TarificationIdEnum, HousingTypeEnum, ProcessTypeEnum } from "../../../Scripts/inpulse/CommonEnums";
import { IContractViewModel, IProductViewModel, IMembershipTypeViewModel } from "../../../Scripts/inpulse/I4UViewModels";
import NewContractStore, { PropertyTypeEnum, INewContractState, IContractSettings, IEnvironmentSettings } from "./new-contract-vuex-store";
import { NewContractEvents } from "./new-contract-vue-events"
import { PublicPathReplacerDirective } from "@/Scripts/vue-directives/publicpath-replacer"

export class PhaseDescriptor {
    name: string;
    id: number;
    constructor(name: string, id: number) {
        this.name = name;
        this.id = id;
    }
}

export class ValidationErrors {
    errors: { [key: string]: string } = {}
    count: number = 0;
    has(key: string): boolean {
        return this.errors[key] != null;
    }
    get(key: string): string {
        return this.errors[key];
    }
    getAllKeys(): string[] {
        let arr: string[] = [];
        for (let key in this.errors)
            arr.push(key);
        return arr;
    }
    getAll(): string[] {
        let arr: string[] = [];
        for (let key in this.errors) {
            arr.push(this.errors[key]);
        }
        return arr;
    }
    hasAny(...keys: string[]) {
        let foundKey = keys.find(x => this.has(x));
        return foundKey != null;
    }
    size(): number {
        return this.count;
    }
    constructor(errors: { [key: string]: string } = null) {
        if (errors != null) {
            for (let key in errors) {
                if (errors.hasOwnProperty(key)) {
                    this.errors[key] = errors[key];
                    this.count++;
                }
            }
        }
    }
}

class PageModel {
    currentPhase: PhaseDescriptor = new PhaseDescriptor(null, null);
    validationErrors: ValidationErrors = new ValidationErrors();
}

export interface INewContractOptions {
    // Id of root container of the page, i.e. container element that holds all the functionality.
    el: string,

    initialPhaseId?: number,

    // List of supported phases.
    phases: PhaseDescriptor[],

    // Vue mixin object to be added into new contract's Vue viewmodel. This can be used to included theme specific Vue components, methods etc.
    mixin?: VueConstructor | ComponentOptions<Vue>,

    autoClearSelectedProduct?: boolean,

    // Username of logged in user.
    moveOutUser?: string,

    isAuthenticated?: boolean,

    validPinFromDb?: boolean,

    // Preselected values that are set when page is initialized.
    preselects?: {
        //moveOutUser: string,
        productId?: number,
        priceListId?: number,
        customerType: CustomerTypeEnum,
        tarification: TarificationIdEnum,
        housingType: HousingTypeEnum,
        campaignCode: string,
        membership?: string,
        brand?: string,
        offerRequest: boolean,
    }
}

export class NewContract {
    private hubStarted: boolean = false;
    private server: any;
    private client: any;
    private options: INewContractOptions;
    private pageModel: PageModel;

    // Vue view model instance. This is public to theme specific implementations,
    // so that they can call functionality possibly added by them selves.
    vue: Vue;

    constructor(options: INewContractOptions) {
        console.log("NewContractVue.constructor()");
        this.server = $.connection.newContractHub.server;
        this.client = $.connection.newContractHub.client;
        this.options = options;
        this.pageModel = new PageModel();
        this.init(options.el, options.mixin);
    }

    getPropertyName(el: HTMLElement, forcedName: string) {
        let name = el.getAttribute("name");
        let propertyName = name;
        if (forcedName != null)
            propertyName = forcedName;
        return propertyName;
    }

    getValidationId(el: HTMLElement, name: string): string {
        var validationId = el.getAttribute("validation-id");
        if (!validationId) { 
            validationId = name ? name : el.getAttribute("name");
        }
        return validationId;
    }

    private directiveEventHandlerMp = (event: Event) => this.directiveEventHandler(event, PropertyTypeEnum.MeteringPointValue);
    private directiveEventHandlerContract = (event: Event) => this.directiveEventHandler(event, PropertyTypeEnum.ContractValue);
    private directiveEventHandlerCustomer = (event: Event) => this.directiveEventHandler(event, PropertyTypeEnum.CustomerValue);
    private directiveEventHandlerBasic = (event: Event) => this.directiveEventHandler(event, PropertyTypeEnum.BasicValue);
    private directiveEventHandlerPlain = (event: Event) => this.directiveEventHandler(event, null);

    private directiveEventHandler = (event: Event, type) => {
        if (event.target instanceof HTMLElement) {
            console.log("v-new-contract-value");
            let el = event.currentTarget as HTMLElement;
            let key = this.getPropertyName(el, null);
            
            let value = null;
            let elAny = el as any;
            if (elAny.value !== undefined)
                value = elAny.value;

            if (el instanceof HTMLInputElement && el.type == "checkbox") {
                // If Vue's true-value and false-value attributes are set, use those when sending data to server.
                let trueValue = el.getAttribute("true-value");
                let falseValue = el.getAttribute("false-value");
                if (el.checked) {
                    value = trueValue != null ? trueValue : true;
                }
                else {
                    value = falseValue != null ? falseValue : false;
                }
            }

            NewContractStore.dispatch("changeValue", { key: key, value: value, type: type })
                .then(() => {
                    this.onChangeValueEvent(key, value);

                    // Trigger custom event that value has been updated. This is triggered on the element.
                    let ev = this.createEvent("value-updated", {
                        detail: {
                            name: key,
                            value: value,
                        }
                    });
                    el.dispatchEvent(ev);
                })
                .catch((msg) => {
                    console.error(msg);
            });
        }
    }

    private validationErrorEventHandler = (event: Event) => this.validationErrorHandler(event);

    private validationErrorHandler = (event: Event) => {
        let el = event.currentTarget as HTMLElement;
        let validationId = this.getValidationId(el, null);
        this.removeValidationError(validationId);
    }

    removeValidationError(validationId: string): void {
        if (this.pageModel.validationErrors.has(validationId)) {
            console.log(`Removing validationError ${validationId}`);
            Vue.delete(this.pageModel.validationErrors.errors, validationId)
        }
    }

    onChangeValueEvent(key: string, value?: string) {
        // TODO: Not very convenient to refer keys with hardcoded strings here, but no time to figure out better way to do this.
        if (key == "MPStreet" || key == "MPZip") {
            // Trigger check for metering point. This may update metering point code automatically from CIS/Fingrid.
            NewContractStore.dispatch("checkMeteringPointAndContract");
        }
    }

    private getEventType(el: HTMLElement, binding: VNodeDirective) {
        let eventType = binding.modifiers.input ? "input" : "change";
        if (el instanceof HTMLButtonElement || binding.modifiers.click)
            eventType = "click";
        return eventType;
    }

    private getDirectiveEventHandler(modifiers: { [key: string]: boolean }) {
        let eh = this.directiveEventHandlerPlain;
        if (modifiers.basic == true) {
            eh = this.directiveEventHandlerBasic;
        }
        else if (modifiers.contract == true) {
            eh = this.directiveEventHandlerContract;
        }
        else if (modifiers.mp == true) {
            eh = this.directiveEventHandlerMp;
        }
        else if (modifiers.customer == true) {
            eh = this.directiveEventHandlerCustomer;
        }
        return eh;
    }

    formSubmitEventHandler = (event: Event) => this.formSubmitHandler(event);

    formSubmitHandler(ev: Event) {
        ev.preventDefault();
        console.log("SUBMIT");
        NewContractStore.dispatch("setloadingNewPhase", { loadingNewPhase: true })

        this.validateContract().then((validationErrors: ValidationErrors) => {
            if (validationErrors.count == 0) {
                let el: any = ev.target;
                el.submit();
            }
            else {
                NewContractStore.dispatch("setloadingNewPhase", { loadingNewPhase: false })
            }
        }).catch(e => {
            console.log("validateContract failed");
            console.log(e);
            NewContractStore.dispatch("setloadingNewPhase", { loadingNewPhase: false })
        });
    }

    initializeStore() {
        console.log("Initialize store!");
        if (this.options.autoClearSelectedProduct != null) {
            NewContractStore.commit("setAutoClearSelectedProduct", this.options.autoClearSelectedProduct);
        }
    }

    init(rootSelector: string, mixin) {
        let self = this;
        Vue.directive("new-contract-value", {
            bind(el: HTMLElement, binding: VNodeDirective, vnode: VNode) {
                if (el.id == "mp-address-zip") {
                    // console.log("BIND");
                    // debugger;
                }
                let eventType = self.getEventType(el, binding);
                let eh = self.getDirectiveEventHandler(binding.modifiers);
                el.addEventListener(eventType, eh);
            },
            unbind(el: HTMLElement, binding: VNodeDirective) {
                let eventType = self.getEventType(el, binding);
                let eh = self.getDirectiveEventHandler(binding.modifiers);
                el.removeEventListener(eventType, eh);
            }
        });

        Vue.directive("new-contract-validate", {
            bind(el: HTMLElement) {
                el.addEventListener("submit", self.formSubmitEventHandler);
            },
            unbind(el: HTMLElement) {
                el.removeEventListener("submit", self.formSubmitEventHandler);
            }
        });

        Vue.directive("public-path-replacer", PublicPathReplacerDirective);

        Vue.directive("validation-error-handler", {
            bind(el: HTMLElement, binding: VNodeDirective, vnode: VNode) {
                let eventType = self.getEventType(el, binding);
                el.addEventListener(eventType, self.validationErrorEventHandler);
            },
            unbind(el: HTMLElement, binding: VNodeDirective, vnode: VNode) {
                let eventType = self.getEventType(el, binding);
                el.removeEventListener(eventType, self.validationErrorEventHandler);
            }
        });

        this.initializeStore();

        this.vue = new Vue({
            el: rootSelector,
            store: NewContractStore,
            data: self.pageModel,
            mixins: mixin != null ? [mixin] : null,
            mounted() {
                self.client.initValues = function (contract: IContractViewModel) {
                    let promises = [];
                    promises.push(self.initValues(contract).then(() =>
                        self.refreshProducts()
                    ));
                    promises.push(self.getMoveOutUser());
                    promises.push(self.getIsUserAuthenticated());
                    promises.push(self.isPinSetInDb());
                    Promise.all(promises).then(() => {
                        console.log("commit setNewContractInitialized");
                        NewContractStore.commit("setNewContractInitialized");
                        NewContractEvents.notifyNewContractInitialized();
                    }).catch(e => {
                        console.log("Promises failed");
                        console.log(e);
                    });
                };

                self.client.showConsumption = function (tuple) {
                    self.consumptionUpdated(tuple.Item1, tuple.Item2);
                }

                self.client.showProducts = function(products: IProductViewModel[]) {
                    console.log("Show products called from hub!");
                    NewContractStore.dispatch("updateProducts", { products: products });
                }

                self.client.showUserName = function (userName: string) {
                    console.log("Show user name called from hub!");
                    NewContractStore.dispatch("updateUserName", { userName: userName });
                }

                self.client.isUserAuthenticated = function (isAuthed: string) {
                    NewContractStore.dispatch("isUserAuthenticated", { isUserAuthenticated: isAuthed });
                }

                self.client.isPinSetInDb = function (pinSet: string) {
                    console.log("isPinSetInDb called from hub!");
                    NewContractStore.dispatch("isPinSetInDb", { pinSet: pinSet });
                }

                $.connection.hub.start().done(function() {
                    console.log("Hub started!");
                    self.hubStarted = true;
                    self.initSettings().then(function () {
                        if (self.isMoving()) {
                            self.initMoving();
                        } else {
                            self.initContract();
                        }
                    });
                    NewContractStore.dispatch("getHolidays", { years: 1 });
                    NewContractStore.dispatch("getMembershipTypes");
                });
            },
            methods: {
                setPhase(phaseName: string, phaseId: number) {
                    console.log("Set phase! " + phaseName + " " + phaseId);
                    let currPhaseNdx = self.options.phases.findIndex(x => x.id == self.pageModel.currentPhase.id);
                    let newPhaseNdx = self.options.phases.findIndex(x => x.id == phaseId);

                    this.$store.dispatch("setloadingNewPhase", { loadingNewPhase: true })
                    // Perform validation only if we are moving forward.
                    if (currPhaseNdx < newPhaseNdx) {
                        self.validateContract().then((validationErrors: ValidationErrors) => {
                            if (validationErrors.count == 0) {
                                self.setNewContractValue("Phase", "" + phaseId).then(() => {
                                    self.pageModel.currentPhase.name = phaseName;
                                    self.pageModel.currentPhase.id = phaseId;
                                }).catch(e => {
                                    console.log("setNewContractValue failed");
                                    console.log(e);
                                });
                            }
                        }).catch(e => {
                            console.log("setNewContractValue failed - 1");
                            console.log(e);
                        }).finally(() => {
                            this.$store.dispatch("setloadingNewPhase", { loadingNewPhase: false })
                        });
                    }
                    else {
                        self.setNewContractValue("Phase", "" + phaseId).then(() => {
                            self.pageModel.currentPhase.name = phaseName;
                            self.pageModel.currentPhase.id = phaseId;
                        }).catch(e => {
                            console.log("setNewContractValue failed - 2");
                            console.log(e);
                        }).finally(() => {
                            this.$store.dispatch("setloadingNewPhase", { loadingNewPhase: false })
                        });
                    }
                },
                notifyCustomError(errors: ValidationErrors) {
                    self.pageModel.validationErrors = errors;
                    self.notifyValidationFailed(errors);
                }
            }
        });
    }

    validateContract(): Promise<ValidationErrors> {
        let self = this;
        return new Promise<ValidationErrors>((resolve, reject) => {
            let contract: IContractViewModel = self.vue.$store.state.contract;
            //let validateMovingProcessByNewContractValidation: boolean = self.vue.$store.state.validateMovingProcessByNewContractValidation;
            let phase: number = contract.Phase;
            console.log("Validating phase [" + phase + "], isMoving: " + this.isMoving() + ", isMovingProcess: " + this.isMovingProcess());
            try {
                if (this.isMoving()) {// } && validateMovingProcessByNewContractValidation == false) {
                    
                    this.server.validateMoving(phase).done(function (validationErrors) {
                        console.log("Move out validation result:");
                        console.log(validationErrors);

                        let errors: { [key: string]: string } = validationErrors.Errors;
                        self.pageModel.validationErrors = new ValidationErrors(errors);

                        if (self.pageModel.validationErrors.count != 0) {
                            self.notifyValidationFailed(self.pageModel.validationErrors);
                        }
                        resolve(self.pageModel.validationErrors);
                    }).fail(() => reject("Error while performing move out validation!"));

                } else {
                    this.server.validateNewContract(phase).done(function (validationErrors) {
                        console.log("New contract validation result:");
                        console.log(validationErrors);

                        let errors: { [key: string]: string } = validationErrors.Errors;
                        self.pageModel.validationErrors = new ValidationErrors(errors);
                        if (self.pageModel.validationErrors.count != 0) {
                            self.notifyValidationFailed(self.pageModel.validationErrors);
                        }
                        resolve(self.pageModel.validationErrors);
                    }).fail(() => reject("Error while performing new contract validation!"));
                }
            }
            catch
            {
                NewContractEvents.notifySessionExpired();
            }
        });
        
    }

    setNewContractValue(key: string, value: string) {
        return this.vue.$store.dispatch("changeValue", { key: key, value: value });
    }

    // store settings initialzations
    initSettings() {
        let promises = [];
        promises.push(this.initContractSettings());
        promises.push(this.initEnvironmentSettings());
        return Promise.all(promises);
    }

    initContractSettings() {
        let self = this;
        return new Promise<void>((resolve, reject) => {
            return this.server.contractSettings().done((settings: IContractSettings) => {
                self.vue.$store.state.contractSettings = settings;
                resolve();
            }).fail(() => reject());
        });
    }


    initEnvironmentSettings() {
        let self = this;
        return new Promise<void>((resolve, reject) => {
            return this.server.environmentSettings().done((settings: IEnvironmentSettings) => {
                self.vue.$store.state.environmentSettings = settings;
                resolve();
            }).fail(() => reject());
        });
    }
    // store settings initialzations

    private initContract() {
        return new Promise<void>((resolve, reject) => {
            // Trigger new contract hub to call initValues on client side.
            this.server.initContract().done(() => {
                console.log("initContract() done");
                resolve();
            }).fail(() => reject());
        });
    }

    private initMoving() {
        return new Promise<void>((resolve, reject) => {
            // Trigger new contract hub to call initMoving on client side.
            this.server.initMoving().done(() => {
                console.log("initMoving() done");
                resolve();
            }).fail(() => reject());
        });
    }

    private refreshProducts(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.server.refreshProducts(null).done(() => {
                console.log("refreshProducts()");
                resolve();
            }).fail(e => {
                console.log("refreshProducts() failed");
                console.log(e);
                reject();
            });
        });
    }

    private getMoveOutUser(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.server.getUserName().done(() => {
                console.log("getMoveOutUser()");
                resolve();
            }).fail(() => reject());
        });
    }

    private getIsUserAuthenticated(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.server.getIsUserAuthed().done(() => {
                resolve();
            }).fail(() => reject());
        });
    }

    private isPinSetInDb(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.server.hasCustomerValidPin("").done(() => {
                console.log("HasCustomerValidPin()");
                resolve();
            }).fail(() => reject());
        });
    }

    /*getActiveContracts(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.server.getActiveContracts().done(() => {
                console.log("getActiveContracts()");
                resolve();
            }).fail(() => reject());
        });
    }*/

    private isMoving(): boolean {
        return this.options.phases[0].name.indexOf("move-out") > -1;
    }

    private isMovingProcess(): boolean {
        return this.isMoving() && this.vue.$store.state.contract.ProcessType == ProcessTypeEnum.MOVINGPROCESS;
    }

    initPreSelectedValues(): Promise<void> {
        let self = this;
        let contract: IContractViewModel = this.vue.$store.state.contract;
        let preselects = this.options.preselects;

        let promises: Promise<void>[] = [];

        let payloads: { key: string, value: string, type?: PropertyTypeEnum }[] = [];

        if (this.options.initialPhaseId != null) {
            payloads.push({ key: "Phase", value: this.options.initialPhaseId.toString() });
        }
        if (this.options.isAuthenticated !== undefined && this.options.isAuthenticated !== null) {
            NewContractStore.commit("isAuthenticated", this.options.isAuthenticated);
        }
        
        let productId = contract.ProductId;
        let priceListId = contract.PriceListId;
        if (preselects != null) {
            if (contract.CustomerType == CustomerTypeEnum.NOTSET && preselects.customerType != null && preselects.customerType != CustomerTypeEnum.NOTSET) {
                payloads.push({ key: "customerType", value: preselects.customerType.toString() });
            }
            // TarificationId (TarificationIdEnum) does not have type 0
            // tarification (TarificationIdEnum) is always different than 0
            //if (contract.TarificationId == 0 && preselects.tarification != null && preselects.tarification != 0) {
            //    payloads.push({ key: "tarification", value: preselects.tarification.toString() });
            //}
            if (contract.HousingTypeId == HousingTypeEnum.UNKNOWN && preselects.housingType != null && preselects.housingType != HousingTypeEnum.UNKNOWN) {
                payloads.push({ key: "housingType", value: preselects.housingType.toString() });
            }
            if (!contract.CampaignCode && preselects.campaignCode)
                payloads.push({ key: "campaignCode", value: preselects.campaignCode });
            if (preselects.brand) {
                payloads.push({ key: "Brand", value: preselects.brand });
            }
            
            if ((productId == 0 || priceListId == 0) && preselects.productId != null && preselects.priceListId != null) {
                productId = preselects.productId;
                priceListId = preselects.priceListId;
            }
            
            // tarification (TarificationIdEnum) is always different than 0
            //if (preselects.productId != null && preselects.priceListId != null && preselects.tarification != null && preselects.tarification != 0) {
            //    NewContractStore.commit("setPreSelectedProductPriceListTarification", true);
            //}
            if (preselects.productId != null && preselects.productId > 0 && preselects.priceListId != null && preselects.priceListId > 0) {
                if (preselects.tarification != null && preselects.tarification > 0) {
                    NewContractStore.commit("setPreSelectedProductPriceListTarification", true);
                }                
                NewContractStore.commit("setPreSelectedProductId", preselects.productId);
                NewContractStore.commit("setPreSelectedPriceListId", preselects.priceListId);
                payloads.push({ key: "PreSelectedProductAndPriceList", value: "true" });
            }
            if (preselects.offerRequest) {
                NewContractStore.commit("offerRequest", preselects.offerRequest);
            }
        }
        
        if (productId && priceListId)
            promises.push(this.vue.$store.dispatch("selectProductById", { productId: productId, priceListId: priceListId }));
        if (payloads.length != 0)
            promises.push(this.vue.$store.dispatch("changeValues", payloads));
      
        if (preselects.membership) {
            NewContractStore.commit("setPreSelectedMembership", preselects.membership);
        }
        return Promise.all(promises).then(() => {
            return Promise.resolve();
        });
    }

    initValues(contract: IContractViewModel): Promise<void> {
        return new Promise<void>((resolve, reject) => {

            let state: INewContractState = this.vue.$store.state;
            state.contract = contract;
            for (var i = 0; i < contract.ExtraFeatures.length; i++) {
                if (contract.ExtraFeatures[i].Name) {
                    let ef = contract.ExtraFeatures[i].Name.split(';');
                    if (ef[0].toLowerCase() == "serviceproduct") {
                        state.selectedServiceProducts.push(ef[1] + ";" + ef[2]);
                    }
                }
            }
            if ((contract.ContractMembership && contract.ContractMembership.MembershipType != null) || contract.Customer.CustomerMemberships.length > 0) {
                if (contract.ContractMembership && contract.ContractMembership.MembershipType != null) {
                    state.contractMembershipTemp = contract.ContractMembership;
                }
                else if (contract.Customer.CustomerMemberships.length > 0) {
                    state.contractMembershipTemp.MembershipName = contract.Customer.CustomerMemberships[0].MembershipName;
                    state.contractMembershipTemp.MembershipType = contract.Customer.CustomerMemberships[0].MembershipTypeId;
                }
                NewContractStore.dispatch("changeValue", { key: "MembershipType", value: null });
            }
            this.initPreSelectedValues().then(() => {
                console.log("Preselected values initialized!");
                //console.log(this.options.phases);
                // Set phase on the client side to match what is stored on the server.
                let phase = this.options.phases.find(x => x.id == state.contract.Phase);
                //console.log(phase);
                if (phase == null) {
                    console.error("Phase [" + contract.Phase + "] not found!");
                    // Phase not found (it's a bug), let's just go to first phase.
                    phase = this.options.phases[0];
                }
                console.log(phase);
                this.pageModel.currentPhase = phase;
                console.log(this.pageModel);
                resolve();

            }).catch((msg) => {

                console.error("Preselect initialization failed: " + msg);
                reject();

            });
        });
    }

    consumptionUpdated(energy1: number, energy2: number) {
        console.log("Consumption updated!");
        this.vue.$store.commit("consumptionUpdated", { energy1: energy1, energy2: energy2 });
    }

    notifyValidationFailed(validationErrors: ValidationErrors) {
        NewContractEvents.notifyValidationFailed(validationErrors);
    }

    private createEvent(ev: string, params: { bubbles?: boolean, cancelable?: boolean, detail: any }) {
        params = params || { bubbles: false, cancelable: false, detail: undefined };
        var evt = document.createEvent('CustomEvent');
        evt.initCustomEvent(ev, params.bubbles, params.cancelable, params.detail);
        return evt;
    }
}
